import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, Package, Users, ShoppingCart, Mail, Globe, Upload, Download, CheckSquare, Square, Eye, EyeOff, Settings, Save } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { language, switchLanguage, t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'dewalt-batteries',
    image_url: '',
    stock: '100',
    is_bestseller: false
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/');
      return;
    }

    if (isAdmin) {
      fetchData();
    }
  }, [user, isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, ordersRes, contactsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/products`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/settings`)
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setContacts(contactsRes.data);
      setSiteSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'pl' ? 'Plik jest za duży (max 5MB)' : 'Файл слишком большой (макс 5МБ)');
      return;
    }
    
    setUploadingImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setFormData({ ...formData, image_url: response.data.url });
        toast.success(language === 'pl' ? 'Zdjęcie przesłane!' : 'Фото загружено!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(language === 'pl' ? 'Błąd przesyłania zdjęcia' : 'Ошибка загрузки фото');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock)
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData);
        toast.success(t.common.success);
      } else {
        await axios.post(`${API}/admin/products`, productData);
        toast.success(t.common.success);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Product save error:', error);
      toast.error(t.common.error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url,
      stock: product.stock.toString(),
      is_bestseller: product.is_bestseller
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm(t.common.deleteConfirm)) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success(t.common.success);
      setSelectedProducts(prev => prev.filter(id => id !== productId));
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t.common.error);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    if (!window.confirm(t.admin.bulkDeleteConfirm)) return;

    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/admin/products/bulk-delete`, {
        product_ids: selectedProducts
      });
      toast.success(`${response.data.deleted} ${t.admin.productsDeleted}`);
      setSelectedProducts([]);
      fetchData();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(t.common.error);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Toggle single product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Toggle all products selection
  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  // Toggle product visibility (hide/show)
  const toggleVisibility = async (productId) => {
    try {
      const response = await axios.patch(`${API}/admin/products/${productId}/toggle-visibility`);
      toast.success(response.data.is_hidden ? t.admin.hidden : t.admin.visible);
      fetchData();
    } catch (error) {
      console.error('Toggle visibility error:', error);
      toast.error(t.common.error);
    }
  };

  // Site Settings handlers
  const updateSettingsField = (section, field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const addFaqItem = () => {
    setSiteSettings(prev => ({
      ...prev,
      faq: [...(prev.faq || []), { question_pl: '', question_ru: '', answer_pl: '', answer_ru: '' }]
    }));
  };

  const updateFaqItem = (index, field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      faq: prev.faq.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeFaqItem = (index) => {
    setSiteSettings(prev => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index)
    }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, siteSettings);
      toast.success(t.admin.settingsSaved);
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error(t.common.error);
    } finally {
      setSavingSettings(false);
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'dewalt-batteries',
      image_url: '',
      stock: '100',
      is_bestseller: false
    });
  };

  // CSV Template Download
  const downloadCsvTemplate = () => {
    const headers = ['name', 'description', 'price', 'category', 'image_url', 'stock', 'is_bestseller'];
    const exampleRow = [
      'Example Battery 20V',
      'High capacity lithium-ion battery',
      '299.99',
      'dewalt-batteries',
      'https://example.com/image.jpg',
      '50',
      'false'
    ];
    const csvContent = [headers.join(','), exampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'products_template.csv';
    link.click();
  };

  // CSV Import Handler
  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or has no data rows');
        return;
      }

      // Parse header
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      
      // Map column indices
      const nameIdx = headers.findIndex(h => h === 'name' || h === 'nazwa');
      const descIdx = headers.findIndex(h => h === 'description' || h === 'opis');
      const priceIdx = headers.findIndex(h => h === 'price' || h === 'cena');
      const categoryIdx = headers.findIndex(h => h === 'category' || h === 'kategoria');
      const imageIdx = headers.findIndex(h => h === 'image_url' || h === 'url_obrazu' || h === 'image');
      const stockIdx = headers.findIndex(h => h === 'stock' || h === 'stan_magazynowy' || h === 'quantity');
      const bestsellerIdx = headers.findIndex(h => h === 'is_bestseller' || h === 'bestseller');

      if (nameIdx === -1 || priceIdx === -1) {
        toast.error('CSV must have "name" and "price" columns');
        return;
      }

      // Parse data rows
      const products = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0 || !values[nameIdx]?.trim()) continue;

        const product = {
          name: values[nameIdx]?.trim() || '',
          description: descIdx >= 0 ? (values[descIdx]?.trim() || '') : '',
          price: parseFloat(values[priceIdx]?.replace(',', '.') || '0') || 0,
          category: categoryIdx >= 0 ? (values[categoryIdx]?.trim() || 'tools-accessories') : 'tools-accessories',
          image_url: imageIdx >= 0 ? (values[imageIdx]?.trim() || '') : '',
          stock: stockIdx >= 0 ? (parseInt(values[stockIdx]) || 100) : 100,
          is_bestseller: bestsellerIdx >= 0 ? (values[bestsellerIdx]?.toLowerCase() === 'true' || values[bestsellerIdx] === '1') : false
        };

        if (product.name && product.price > 0) {
          products.push(product);
        }
      }

      if (products.length === 0) {
        toast.error('No valid products found in CSV');
        return;
      }

      // Send to backend
      const response = await axios.post(`${API}/admin/products/bulk-import`, { products });
      
      toast.success(`${response.data.created} ${t.admin.productsCreated}`);
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} ${t.admin.productsFailed}`);
      }
      
      setImportDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(t.admin.importError);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Helper to parse CSV line (handles quoted values)
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getCategoryName = (category) => {
    switch(category) {
      case 'dewalt-batteries': return t.nav.powerTools;
      case 'milwaukee-batteries': return t.nav.handTools;
      case 'makita-batteries': return t.nav.accessories;
      case 'tools-accessories': return t.nav.toolsAccessories;
      default: return category;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-yellow-400" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <div className="bg-[#111111] border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-2 block">{t.admin.dashboard}</span>
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white" data-testid="admin-title">{t.admin.title}</h1>
            </div>
            
            {/* Language Toggle */}
            <div className="flex items-center gap-3 bg-[#050505] border border-zinc-800 p-2">
              <Globe size={18} className="text-yellow-400" />
              <span className="font-mono text-xs tracking-widest uppercase text-zinc-400">{t.admin.language}:</span>
              <button 
                onClick={() => switchLanguage('pl')}
                className={`px-3 py-1 font-mono text-xs tracking-widest uppercase transition-colors ${language === 'pl' ? 'bg-yellow-400 text-black' : 'text-zinc-400 hover:text-white'}`}
                data-testid="admin-lang-pl"
              >
                🇵🇱 PL
              </button>
              <button 
                onClick={() => switchLanguage('ru')}
                className={`px-3 py-1 font-mono text-xs tracking-widest uppercase transition-colors ${language === 'ru' ? 'bg-yellow-400 text-black' : 'text-zinc-400 hover:text-white'}`}
                data-testid="admin-lang-ru"
              >
                🇷🇺 RU
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="border border-zinc-800 bg-[#111111] p-4">
            <Package size={24} className="text-yellow-400 mb-2" />
            <p className="font-mono text-2xl text-white">{products.length}</p>
            <p className="text-zinc-500 text-sm">{t.admin.products}</p>
          </div>
          <div className="border border-zinc-800 bg-[#111111] p-4">
            <ShoppingCart size={24} className="text-yellow-400 mb-2" />
            <p className="font-mono text-2xl text-white">{orders.length}</p>
            <p className="text-zinc-500 text-sm">{t.admin.orders}</p>
          </div>
          <div className="border border-zinc-800 bg-[#111111] p-4">
            <Users size={24} className="text-yellow-400 mb-2" />
            <p className="font-mono text-2xl text-white">{formatPrice(orders.reduce((sum, o) => sum + o.total, 0))}</p>
            <p className="text-zinc-500 text-sm">{t.admin.revenue}</p>
          </div>
          <div className="border border-zinc-800 bg-[#111111] p-4">
            <Mail size={24} className="text-yellow-400 mb-2" />
            <p className="font-mono text-2xl text-white">{contacts.length}</p>
            <p className="text-zinc-500 text-sm">{t.admin.messages}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-[#111111] border border-zinc-800 rounded-none p-1 mb-6">
            <TabsTrigger value="products" className="rounded-none data-[state=active]:bg-yellow-400 data-[state=active]:text-black" data-testid="tab-products">
              {t.admin.products}
            </TabsTrigger>
            <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-yellow-400 data-[state=active]:text-black" data-testid="tab-orders">
              {t.admin.orders}
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none data-[state=active]:bg-yellow-400 data-[state=active]:text-black" data-testid="tab-contacts">
              {t.admin.contacts}
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-yellow-400 data-[state=active]:text-black" data-testid="tab-settings">
              <Settings size={16} className="mr-1" /> {t.admin.settings}
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-2xl uppercase text-white">{t.admin.products}</h2>
                {selectedProducts.length > 0 && (
                  <span className="bg-yellow-400 text-black text-xs font-bold px-2 py-1">
                    {selectedProducts.length} {t.admin.selected}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {/* Bulk Delete Button */}
                {selectedProducts.length > 0 && (
                  <button 
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="bg-red-600 text-white uppercase font-bold tracking-widest px-4 py-3 border border-red-600 hover:bg-red-700 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                    data-testid="bulk-delete-btn"
                  >
                    {bulkDeleting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    {t.admin.deleteSelected} ({selectedProducts.length})
                  </button>
                )}

                {/* Download CSV Template */}
                <button 
                  onClick={downloadCsvTemplate}
                  className="bg-transparent text-zinc-400 uppercase font-bold tracking-widest px-4 py-3 border border-zinc-700 hover:border-white hover:text-white transition-colors flex items-center gap-2 text-sm"
                  data-testid="download-csv-template-btn"
                >
                  <Download size={16} /> {t.admin.downloadTemplate}
                </button>

                {/* Import CSV Dialog */}
                <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                  <DialogTrigger asChild>
                    <button className="bg-transparent text-zinc-400 uppercase font-bold tracking-widest px-4 py-3 border border-zinc-700 hover:border-yellow-400 hover:text-yellow-400 transition-colors flex items-center gap-2 text-sm" data-testid="import-csv-btn">
                      <Upload size={16} /> {t.admin.importCsv}
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#111111] border-zinc-800 rounded-none max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl uppercase text-white">
                        {t.admin.importCsv}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <p className="text-zinc-400 text-sm">{t.admin.csvFormatInfo}</p>
                      <p className="text-zinc-500 text-xs font-mono">
                        Categories: dewalt-batteries, milwaukee-batteries, makita-batteries, tools-accessories
                      </p>
                      <div className="border-2 border-dashed border-zinc-700 p-6 text-center hover:border-yellow-400 transition-colors">
                        <input 
                          type="file" 
                          accept=".csv" 
                          onChange={handleCsvImport}
                          ref={fileInputRef}
                          className="hidden" 
                          id="csv-upload"
                          data-testid="csv-file-input"
                        />
                        <label htmlFor="csv-upload" className="cursor-pointer">
                          <Upload size={32} className="mx-auto text-zinc-500 mb-3" />
                          <p className="text-zinc-300 text-sm mb-1">{t.admin.selectCsvFile}</p>
                          <p className="text-zinc-500 text-xs">.csv</p>
                        </label>
                      </div>
                      {importing && (
                        <div className="flex items-center justify-center gap-2 text-yellow-400">
                          <Loader2 size={20} className="animate-spin" />
                          <span>{t.common.loading}</span>
                        </div>
                      )}
                      <button 
                        onClick={downloadCsvTemplate}
                        className="w-full bg-transparent text-zinc-400 uppercase font-bold tracking-widest px-4 py-3 border border-zinc-700 hover:border-white hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
                      >
                        <Download size={16} /> {t.admin.downloadTemplate}
                      </button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Add Product Dialog */}
                <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <button className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-6 py-3 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors flex items-center gap-2" data-testid="admin-add-product-btn">
                      <Plus size={20} /> {t.admin.addProduct}
                    </button>
                  </DialogTrigger>
                <DialogContent className="bg-[#111111] border-zinc-800 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase text-white">
                      {editingProduct ? t.admin.editProduct : t.admin.addProduct}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div>
                      <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.admin.productName}</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 w-full focus:border-yellow-400 focus:outline-none" data-testid="product-name-input" />
                    </div>
                    <div>
                      <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.admin.description}</label>
                      <textarea name="description" value={formData.description} onChange={handleChange} required rows={3} className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 w-full focus:border-yellow-400 focus:outline-none resize-none" data-testid="product-description-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.admin.price}</label>
                        <input type="number" name="price" value={formData.price} onChange={handleChange} required step="0.01" min="0" className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 w-full focus:border-yellow-400 focus:outline-none" data-testid="product-price-input" />
                      </div>
                      <div>
                        <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.admin.stock}</label>
                        <input type="number" name="stock" value={formData.stock} onChange={handleChange} required min="0" className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 w-full focus:border-yellow-400 focus:outline-none" data-testid="product-stock-input" />
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.admin.category}</label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                        <SelectTrigger className="bg-[#050505] border-zinc-700 text-white rounded-none" data-testid="product-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111111] border-zinc-700 rounded-none">
                          <SelectItem value="dewalt-batteries">{t.nav.powerTools}</SelectItem>
                          <SelectItem value="milwaukee-batteries">{t.nav.handTools}</SelectItem>
                          <SelectItem value="makita-batteries">{t.nav.accessories}</SelectItem>
                          <SelectItem value="tools-accessories">{t.nav.toolsAccessories}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.admin.imageUrl}</label>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input type="url" name="image_url" value={formData.image_url} onChange={handleChange} className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 flex-1 focus:border-yellow-400 focus:outline-none" placeholder="https://..." data-testid="product-image-input" />
                          <input 
                            type="file" 
                            ref={imageInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                          />
                          <button 
                            type="button"
                            onClick={() => imageInputRef.current?.click()}
                            disabled={uploadingImage}
                            className="bg-zinc-800 text-white px-4 py-3 border border-zinc-700 hover:bg-zinc-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                          >
                            {uploadingImage ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <Upload size={18} />
                            )}
                          </button>
                        </div>
                        {formData.image_url && (
                          <div className="w-20 h-20 bg-zinc-800 border border-zinc-700">
                            <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" name="is_bestseller" checked={formData.is_bestseller} onChange={handleChange} id="bestseller" className="w-4 h-4" data-testid="product-bestseller-checkbox" />
                      <label htmlFor="bestseller" className="text-zinc-300 text-sm">{t.admin.markAsBestseller}</label>
                    </div>
                    <button type="submit" className="w-full bg-yellow-400 text-black uppercase font-bold tracking-widest px-6 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors" data-testid="product-submit-btn">
                      {editingProduct ? t.admin.updateProduct : t.admin.createProduct}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            <div className="border border-zinc-800 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#111111] border-b border-zinc-800">
                    <th className="px-4 py-3 text-left w-12">
                      <button 
                        onClick={toggleSelectAll}
                        className="text-zinc-400 hover:text-yellow-400 transition-colors"
                        data-testid="select-all-checkbox"
                        title={t.admin.selectAll}
                      >
                        {selectedProducts.length === products.length && products.length > 0 ? (
                          <CheckSquare size={20} className="text-yellow-400" />
                        ) : (
                          <Square size={20} />
                        )}
                      </button>
                    </th>
                    <th className="font-mono text-xs tracking-widest uppercase text-zinc-400 px-4 py-3 text-left">{t.admin.products}</th>
                    <th className="font-mono text-xs tracking-widest uppercase text-zinc-400 px-4 py-3 text-left">{t.admin.category}</th>
                    <th className="font-mono text-xs tracking-widest uppercase text-zinc-400 px-4 py-3 text-left">{t.common.price}</th>
                    <th className="font-mono text-xs tracking-widest uppercase text-zinc-400 px-4 py-3 text-left">{t.admin.stock}</th>
                    <th className="font-mono text-xs tracking-widest uppercase text-zinc-400 px-4 py-3 text-left">{t.common.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr 
                      key={product.id} 
                      className={`border-b border-zinc-800 hover:bg-[#111111] ${selectedProducts.includes(product.id) ? 'bg-yellow-400/10' : ''}`} 
                      data-testid={`admin-product-${product.id}`}
                    >
                      <td className="px-4 py-3">
                        <button 
                          onClick={() => toggleProductSelection(product.id)}
                          className="text-zinc-400 hover:text-yellow-400 transition-colors"
                          data-testid={`select-product-${product.id}`}
                        >
                          {selectedProducts.includes(product.id) ? (
                            <CheckSquare size={20} className="text-yellow-400" />
                          ) : (
                            <Square size={20} />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-zinc-800 flex-shrink-0 flex items-center justify-center">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <span className="text-zinc-600 text-xs">N/A</span>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-bold ${product.is_hidden ? 'text-zinc-500' : 'text-white'}`}>{product.name}</p>
                              {product.is_hidden && <span className="bg-zinc-700 text-zinc-400 text-xs px-2 py-0.5">{t.admin.hidden}</span>}
                            </div>
                            {product.is_bestseller && <span className="text-yellow-500 text-xs">{t.product.bestseller}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-400">{getCategoryName(product.category)}</td>
                      <td className="px-4 py-3 font-mono text-yellow-500">{formatPrice(product.price)}</td>
                      <td className="px-4 py-3 text-zinc-300">{product.stock}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => toggleVisibility(product.id)} 
                            className={`p-2 transition-colors ${product.is_hidden ? 'text-zinc-600 hover:text-green-500' : 'text-zinc-400 hover:text-orange-500'}`}
                            title={product.is_hidden ? t.admin.showProduct : t.admin.hideProduct}
                            data-testid={`toggle-visibility-${product.id}`}
                          >
                            {product.is_hidden ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                          <button onClick={() => handleEdit(product)} className="p-2 text-zinc-400 hover:text-white" data-testid={`edit-product-${product.id}`}>
                            <Pencil size={18} />
                          </button>
                          <button onClick={() => handleDelete(product.id)} className="p-2 text-zinc-400 hover:text-red-500" data-testid={`delete-product-${product.id}`}>
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <h2 className="font-heading text-2xl uppercase text-white mb-6">{t.admin.orders}</h2>
            {orders.length === 0 ? (
              <p className="text-zinc-400 text-center py-8">{t.admin.noOrders}</p>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="border border-zinc-800 bg-[#111111] p-4" data-testid={`admin-order-${order.id}`}>
                    <div className="flex flex-wrap gap-4 justify-between items-start mb-4">
                      <div>
                        <span className="font-mono text-xs text-zinc-500">{t.orders.orderId}:</span>
                        <span className="font-mono text-sm text-yellow-400 ml-2">{order.id.slice(0, 8)}...</span>
                      </div>
                      <div>
                        <span className="font-mono text-xs text-zinc-500">{t.orders.date}:</span>
                        <span className="text-zinc-300 text-sm ml-2">{formatDate(order.created_at)}</span>
                      </div>
                      <div>
                        <span className="font-mono text-xs text-zinc-500">{t.orders.status}:</span>
                        <span className="text-yellow-400 text-sm ml-2 capitalize">{order.status}</span>
                      </div>
                      <div>
                        <span className="font-mono text-xs text-zinc-500">{t.orders.total}:</span>
                        <span className="font-mono text-yellow-500 ml-2">{formatPrice(order.total)}</span>
                      </div>
                    </div>
                    <div className="text-sm text-zinc-400">
                      <p>{t.admin.shipTo} {order.shipping_info.full_name}, {order.shipping_info.city}</p>
                      <p>{order.items.length} {t.checkout.items}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <h2 className="font-heading text-2xl uppercase text-white mb-6">{t.admin.contactMessages}</h2>
            {contacts.length === 0 ? (
              <p className="text-zinc-400 text-center py-8">{t.admin.noMessages}</p>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="border border-zinc-800 bg-[#111111] p-4" data-testid={`admin-contact-${contact.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-white font-bold">{contact.name}</p>
                        <p className="text-zinc-400 text-sm">{contact.email}</p>
                      </div>
                      <span className="text-zinc-500 text-xs">{formatDate(contact.created_at)}</span>
                    </div>
                    <p className="text-zinc-300 text-sm mt-2">{contact.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-2xl uppercase text-white">{t.admin.siteSettings}</h2>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-6 py-3 hover:bg-yellow-300 transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="save-settings-btn"
              >
                {savingSettings ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {t.admin.saveSettings}
              </button>
            </div>

            {siteSettings && (
              <div className="space-y-8">
                {/* Contact Info Section */}
                <div className="border border-zinc-800 bg-[#111111] p-6">
                  <h3 className="font-heading text-xl uppercase text-yellow-400 mb-4">{t.admin.contactInfo}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.phone}</label>
                      <input
                        type="text"
                        value={siteSettings.contact?.phone || ''}
                        onChange={(e) => updateSettingsField('contact', 'phone', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.email}</label>
                      <input
                        type="email"
                        value={siteSettings.contact?.email || ''}
                        onChange={(e) => updateSettingsField('contact', 'email', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.address}</label>
                      <input
                        type="text"
                        value={siteSettings.contact?.address || ''}
                        onChange={(e) => updateSettingsField('contact', 'address', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.workingHours}</label>
                      <input
                        type="text"
                        value={siteSettings.contact?.working_hours || ''}
                        onChange={(e) => updateSettingsField('contact', 'working_hours', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                  </div>
                  <h4 className="font-mono text-sm uppercase text-zinc-500 mt-6 mb-3">{t.admin.socialMedia}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">Facebook</label>
                      <input
                        type="text"
                        value={siteSettings.contact?.facebook || ''}
                        onChange={(e) => updateSettingsField('contact', 'facebook', e.target.value)}
                        placeholder="https://facebook.com/..."
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">Instagram</label>
                      <input
                        type="text"
                        value={siteSettings.contact?.instagram || ''}
                        onChange={(e) => updateSettingsField('contact', 'instagram', e.target.value)}
                        placeholder="https://instagram.com/..."
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">YouTube</label>
                      <input
                        type="text"
                        value={siteSettings.contact?.youtube || ''}
                        onChange={(e) => updateSettingsField('contact', 'youtube', e.target.value)}
                        placeholder="https://youtube.com/..."
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* About Us Section */}
                <div className="border border-zinc-800 bg-[#111111] p-6">
                  <h3 className="font-heading text-xl uppercase text-yellow-400 mb-4">{t.admin.aboutUs}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.aboutTitle} ({t.admin.polish})</label>
                      <input
                        type="text"
                        value={siteSettings.about?.title_pl || ''}
                        onChange={(e) => updateSettingsField('about', 'title_pl', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.aboutTitle} ({t.admin.russian})</label>
                      <input
                        type="text"
                        value={siteSettings.about?.title_ru || ''}
                        onChange={(e) => updateSettingsField('about', 'title_ru', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.aboutDescription} ({t.admin.polish})</label>
                      <textarea
                        rows={4}
                        value={siteSettings.about?.description_pl || ''}
                        onChange={(e) => updateSettingsField('about', 'description_pl', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.aboutDescription} ({t.admin.russian})</label>
                      <textarea
                        rows={4}
                        value={siteSettings.about?.description_ru || ''}
                        onChange={(e) => updateSettingsField('about', 'description_ru', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.aboutImage} (URL)</label>
                      <input
                        type="text"
                        value={siteSettings.about?.image_url || ''}
                        onChange={(e) => updateSettingsField('about', 'image_url', e.target.value)}
                        placeholder="https://..."
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Homepage Section */}
                <div className="border border-zinc-800 bg-[#111111] p-6">
                  <h3 className="font-heading text-xl uppercase text-yellow-400 mb-4">{t.admin.homepage}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.heroTitle1} ({t.admin.polish})</label>
                      <input
                        type="text"
                        value={siteSettings.homepage?.hero_title1_pl || ''}
                        onChange={(e) => updateSettingsField('homepage', 'hero_title1_pl', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.heroTitle1} ({t.admin.russian})</label>
                      <input
                        type="text"
                        value={siteSettings.homepage?.hero_title1_ru || ''}
                        onChange={(e) => updateSettingsField('homepage', 'hero_title1_ru', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.heroTitle2} ({t.admin.polish})</label>
                      <input
                        type="text"
                        value={siteSettings.homepage?.hero_title2_pl || ''}
                        onChange={(e) => updateSettingsField('homepage', 'hero_title2_pl', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.heroTitle2} ({t.admin.russian})</label>
                      <input
                        type="text"
                        value={siteSettings.homepage?.hero_title2_ru || ''}
                        onChange={(e) => updateSettingsField('homepage', 'hero_title2_ru', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.heroDescription} ({t.admin.polish})</label>
                      <textarea
                        rows={3}
                        value={siteSettings.homepage?.hero_description_pl || ''}
                        onChange={(e) => updateSettingsField('homepage', 'hero_description_pl', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.heroDescription} ({t.admin.russian})</label>
                      <textarea
                        rows={3}
                        value={siteSettings.homepage?.hero_description_ru || ''}
                        onChange={(e) => updateSettingsField('homepage', 'hero_description_ru', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Policies Section */}
                <div className="border border-zinc-800 bg-[#111111] p-6">
                  <h3 className="font-heading text-xl uppercase text-yellow-400 mb-4">{t.admin.policies}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.shippingPolicy} ({t.admin.polish})</label>
                      <textarea
                        rows={3}
                        value={siteSettings.policy?.shipping_pl || ''}
                        onChange={(e) => updateSettingsField('policy', 'shipping_pl', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.shippingPolicy} ({t.admin.russian})</label>
                      <textarea
                        rows={3}
                        value={siteSettings.policy?.shipping_ru || ''}
                        onChange={(e) => updateSettingsField('policy', 'shipping_ru', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.returnsPolicy} ({t.admin.polish})</label>
                      <textarea
                        rows={3}
                        value={siteSettings.policy?.returns_pl || ''}
                        onChange={(e) => updateSettingsField('policy', 'returns_pl', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">{t.admin.returnsPolicy} ({t.admin.russian})</label>
                      <textarea
                        rows={3}
                        value={siteSettings.policy?.returns_ru || ''}
                        onChange={(e) => updateSettingsField('policy', 'returns_ru', e.target.value)}
                        className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                <div className="border border-zinc-800 bg-[#111111] p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-heading text-xl uppercase text-yellow-400">{t.admin.faq}</h3>
                    <button
                      onClick={addFaqItem}
                      className="bg-zinc-800 text-white px-4 py-2 hover:bg-zinc-700 transition-colors flex items-center gap-2"
                    >
                      <Plus size={16} /> {t.admin.addFaq}
                    </button>
                  </div>
                  <div className="space-y-4">
                    {(siteSettings.faq || []).map((faq, index) => (
                      <div key={index} className="border border-zinc-700 p-4 relative">
                        <button
                          onClick={() => removeFaqItem(index)}
                          className="absolute top-2 right-2 text-zinc-500 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-zinc-400 text-sm mb-1">{t.admin.question} ({t.admin.polish})</label>
                            <input
                              type="text"
                              value={faq.question_pl || ''}
                              onChange={(e) => updateFaqItem(index, 'question_pl', e.target.value)}
                              className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-zinc-400 text-sm mb-1">{t.admin.question} ({t.admin.russian})</label>
                            <input
                              type="text"
                              value={faq.question_ru || ''}
                              onChange={(e) => updateFaqItem(index, 'question_ru', e.target.value)}
                              className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-zinc-400 text-sm mb-1">{t.admin.answer} ({t.admin.polish})</label>
                            <textarea
                              rows={2}
                              value={faq.answer_pl || ''}
                              onChange={(e) => updateFaqItem(index, 'answer_pl', e.target.value)}
                              className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                            />
                          </div>
                          <div>
                            <label className="block text-zinc-400 text-sm mb-1">{t.admin.answer} ({t.admin.russian})</label>
                            <textarea
                              rows={2}
                              value={faq.answer_ru || ''}
                              onChange={(e) => updateFaqItem(index, 'answer_ru', e.target.value)}
                              className="w-full bg-[#050505] border border-zinc-700 text-white px-4 py-2 focus:border-yellow-400 focus:outline-none resize-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
